import React, { useEffect, useState } from 'react';
import { packagesService } from '../services/packagesService';
import { XMarkIcon } from '@heroicons/react/24/outline';

const AllBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);
  const [dateError, setDateError] = useState('');

  // Calculate summary from bookings data
  const calculateSummary = (bookingsData) => {
    if (!Array.isArray(bookingsData) || bookingsData.length === 0) {
      return {
        totalCommission: 0,
        totalOriginalFee: 0,
        totalAmount: 0
      };
    }

    let totalCommission = 0;
    let totalOriginalFee = 0;
    let totalAmount = 0;

    bookingsData.forEach(booking => {
      const amount = booking.totalAmount || 0;
      totalAmount += amount;
      
      // Calculate commission (assuming 5% commission rate)
      const commission = Math.round(amount * 0.05);
      totalCommission += commission;
      
      // Calculate original fee (total amount minus commission)
      const originalFee = amount - commission;
      totalOriginalFee += originalFee;
    });

    return {
      totalCommission,
      totalOriginalFee,
      totalAmount,
      _source: 'calculated'
    };
  };

  const validateDates = (start, end) => {
    if (start && end) {
      const startDateObj = new Date(start);
      const endDateObj = new Date(end);
      
      if (endDateObj <= startDateObj) {
        setDateError('End date must be greater than start date');
        return false;
      }
    }
    setDateError('');
    return true;
  };

  const handleStartDateChange = (e) => {
    const newStartDate = e.target.value;
    setStartDate(newStartDate);
    validateDates(newStartDate, endDate);
  };

  const handleEndDateChange = (e) => {
    const newEndDate = e.target.value;
    setEndDate(newEndDate);
    validateDates(startDate, newEndDate);
  };

  const fetchBookings = async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await packagesService.getAllBookings(filters);
      console.log('Full API Response:', response);
      console.log('Summary data received:', response.summary);
      
      const bookingsData = response.data || [];
      setBookings(bookingsData);
      setPagination(response.pagination || null);
      
      // Use API summary if available, otherwise calculate from bookings data
      let summaryData;
      if (response.summary) {
        summaryData = { ...response.summary, _source: 'api' };
      } else {
        summaryData = calculateSummary(bookingsData);
      }
      setSummary(summaryData);
      
      console.log('Summary values (API or calculated):', {
        totalCommission: summaryData.totalCommission,
        totalOriginalFee: summaryData.totalOriginalFee,
        totalAmount: summaryData.totalAmount
      });
      console.log('Summary source:', response.summary ? 'API' : 'Calculated from bookings');
    } catch (err) {
      console.error('Failed to load bookings:', err);
      setError('Failed to load bookings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings({ page: 1, limit });
  }, []);

  const handleApplyFilters = () => {
    // Validate dates before applying filters
    if (!validateDates(startDate, endDate)) {
      return; // Don't apply filters if validation fails
    }
    
    setCurrentPage(1); // Reset to first page when applying filters
    const params = { page: 1, limit };
    if (search) params.search = search;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    fetchBookings(params);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    const params = { page, limit };
    if (search) params.search = search;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    fetchBookings(params);
  };

  const handleClearFilters = () => {
    setSearch('');
    setStartDate('');
    setEndDate('');
    setDateError('');
    setCurrentPage(1);
    fetchBookings({ page: 1, limit });
  };

  if (loading) {
    return (
      <div className="h-full bg-gray-50">
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="px-6 py-4">
            <h1 className="text-2xl font-bold text-gray-900">All Bookings</h1>
            <p className="text-gray-600">Loading...</p>
          </div>
        </div>
        <main className="p-6">
          <div className="flex items-center justify-center min-h-96">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full bg-gray-50">
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="px-6 py-4">
            <h1 className="text-2xl font-bold text-gray-900">All Bookings</h1>
            <p className="text-gray-600">Error loading bookings</p>
          </div>
        </div>
        <main className="p-6">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-50">
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="px-6 py-4">
          <h1 className="text-2xl font-bold text-gray-900">All Bookings</h1>
          <p className="text-gray-600">Fetched {bookings.length} bookings{pagination ? ` • Page ${pagination.currentPage} of ${pagination.totalPages}` : ''}</p>
        </div>
      </div>
      <main className="p-6">
        <div className="card p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Start date</label>
              <input 
                type="date" 
                value={startDate} 
                onChange={handleStartDateChange} 
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  dateError ? 'border-red-300' : 'border-gray-300'
                }`} 
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">End date</label>
              <input 
                type="date" 
                value={endDate} 
                onChange={handleEndDateChange}
                min={startDate || undefined}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  dateError ? 'border-red-300' : 'border-gray-300'
                }`} 
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Search by name</label>
              <input type="text" placeholder="e.g. mukesh" value={search} onChange={(e) => setSearch(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
            </div>
            <div className="flex items-end space-x-2">
              <button 
                onClick={handleApplyFilters} 
                disabled={!!dateError}
                className={`flex-1 py-2 px-4 rounded-md transition-colors ${
                  dateError 
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                    : 'bg-primary-600 text-white hover:bg-primary-700'
                }`}
              >
                Apply
              </button>
              <button 
                onClick={handleClearFilters} 
                className="bg-gray-200 text-gray-600 p-2 rounded-md hover:bg-gray-300 transition-colors"
                title="Clear Filters"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
          
          {/* Small Summary Card */}
          {summary && (
            <div className="mt-4 bg-gray-50 rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">SPARK EARNINGS</p>
                  <p className="text-lg font-bold text-green-600">
                    ₹{summary?.totalCommission?.toLocaleString() || '0.00'}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">VENDOR EARNINGS</p>
                  <p className="text-lg font-bold text-blue-600">
                    ₹{summary?.totalOriginalFee?.toLocaleString() || '0.00'}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">TOTAL AMOUNT</p>
                  <p className="text-lg font-bold text-purple-600">
                    ₹{summary?.totalAmount?.toLocaleString() || '0.00'}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {dateError && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{dateError}</p>
            </div>
          )}
        </div>
        <div className="card p-6">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Package</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dates</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">People</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Array.isArray(bookings) && bookings.length > 0 ? (
                  bookings.map((b) => (
                    <tr key={b._id}>
                      <td className="px-4 py-3 text-sm text-gray-900">{b.name || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{b.emailId || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{b.phoneNumber || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{b.packageId?.title || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{b.dateSlots ? `${new Date(b.dateSlots.from).toLocaleDateString()} → ${new Date(b.dateSlots.to).toLocaleDateString()}` : '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right">{b.numberOfPeople ?? '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right">{typeof b.totalAmount === 'number' ? b.totalAmount.toLocaleString() : '—'}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          b.status === 'CONFIRMED' ? 'bg-green-100 text-green-800' :
                          b.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>{b.status}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">{b.createdAt ? new Date(b.createdAt).toLocaleString() : '—'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td className="px-4 py-6 text-center text-sm text-gray-500" colSpan={9}>No bookings found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {pagination && (
            <div className="mt-4 flex items-center justify-between">
              <div className="text-xs text-gray-500">Page {pagination.currentPage} of {pagination.totalPages} • Total {pagination.totalBookings}</div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={!pagination.hasPrev}
                  className="px-3 py-1 text-xs border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Previous
                </button>
                <span className="text-xs text-gray-500">
                  {pagination.currentPage} / {pagination.totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={!pagination.hasNext}
                  className="px-3 py-1 text-xs border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AllBookings;
